import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import RelatedProducts from '../components/RelatedProducts';
import '../css/ProductDetailPage.css';
import { useCart } from '../context/CartContext';
import { FaPhone, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import { BACKEND_URL, COMPANY_PHONES, COMPANY_EMAIL } from '../config';
import { formatPrice } from '../utils/formatPrice';
import ProductImage from '../components/ProductImage';
import CopyPhoneText from '../components/CopyPhoneText';

/**
 * AccordionSection - Сучасний акордеон з анімаціями 2025
 */

/**
 * ProductDetailPage - Детальна сторінка товару з сучасним UX/UI 2025
 *
 * Особливості:
 * - Mobile-first дизайн
 * - Мінімалістичний інтерфейс
 * - Синій колір #2D6CDF замість чорного
 * - Покращена доступність
 * - Сучасні анімації та мікроінтеракції
 */
const ProductDetailPage = () => {
    const { slug } = useParams();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { addToCart, setIsCartOpen } = useCart();
    
    // Стан для галереї
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);

    const [activeTab, setActiveTab] = useState('product');

    const tabs = [
        { id: 'product', title: 'Опис товару' },
        { id: 'delivery', title: 'Доставка' },
        { id: 'warranty', title: 'Гарантія' },
        { id: 'contacts', title: 'Контакти' },
        { id: 'reviews', title: 'Відгуки' }
    ];

    const switchTab = (tabId) => {
        setActiveTab(tabId);
    };

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
    }, [slug]);

    useEffect(() => {
        const fetchProduct = async () => {
            setLoading(true);
            setError(null);
            setProduct(null);

            const segments = slug.split('-');
            const firstSegment = segments[0];
            const lastSegment = segments[segments.length - 1];
            const productId = /^\d+$/.test(firstSegment)
                ? firstSegment
                : lastSegment;
            const apiUrl = `${BACKEND_URL}/products/${productId}`;

            try {
                const response = await fetch(apiUrl);

                if (!response.ok) {
                    throw new Error(`HTTP помилка! Статус: ${response.status}`);
                }

                const data = await response.json();
                setProduct(data);

                if (window.gtag) {
                    window.gtag('event', 'view_item', {
                        currency: 'UAH',
                        value: parseFloat((data.price || 0).toFixed(2)),
                        items: [{
                            item_id: String(data.id),
                            item_name: data.name_ua,
                            price: parseFloat((data.price || 0).toFixed(2)),
                            quantity: 1,
                            google_business_vertical: 'retail',
                        }],
                    });
                }

            } catch (err) {
                setError(err.message || "Не вдалося завантажити інформацію про продукт.");
            } finally {
                setLoading(false);
            }
        };

        fetchProduct();
    }, [slug]);

    const handleBuyClick = () => {
        if (product) {
            addToCart({
                id: product.id,
                slug: product.slug,
                name_ua: product.name_ua,
                price: product.price,
                photo: product.photo,
            });
            setIsCartOpen(true);
            if (window.gtag) {
                window.gtag('event', 'add_to_cart', {
                    currency: 'UAH',
                    value: parseFloat((product.price || 0).toFixed(2)),
                    items: [{
                        item_id: String(product.id),
                        item_name: product.name_ua,
                        price: parseFloat((product.price || 0).toFixed(2)),
                        quantity: 1,
                        google_business_vertical: 'retail',
                    }],
                });
            }
        }
    };

    // Функції для галереї
    const openGallery = (index = 0) => {
        setCurrentImageIndex(index);
        setIsGalleryOpen(true);
    };

    const closeGallery = () => {
        setIsGalleryOpen(false);
    };

    const nextImage = () => {
        if (product && product.images) {
            setCurrentImageIndex((prev) =>
                prev === product.images.length - 1 ? 0 : prev + 1
            );
        }
    };

    const prevImage = () => {
        if (product && product.images) {
            setCurrentImageIndex((prev) =>
                prev === 0 ? product.images.length - 1 : prev - 1
            );
        }
    };

    const goToImage = (index) => {
        setCurrentImageIndex(index);
    };

    // Обробка клавіатурних подій для галереї
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (!isGalleryOpen) return;

            switch (event.key) {
                case 'Escape':
                    closeGallery();
                    break;
                case 'ArrowLeft':
                    event.preventDefault();
                    prevImage();
                    break;
                case 'ArrowRight':
                    event.preventDefault();
                    nextImage();
                    break;
                default:
                    break;
            }
        };

        if (isGalleryOpen) {
            document.addEventListener('keydown', handleKeyDown);
            // Забороняємо скролінг body
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'unset';
        };
    }, [isGalleryOpen, nextImage, prevImage]);

    // Отримуємо масив зображень (якщо є додаткові фото, інакше використовуємо основне)
    const productImages = product ? (product.images && product.images.length > 0 ? product.images : [product.photo]) : [];

    // Loading state
    if (loading) {
        return (
            <Layout>
                <main className="product-detail-content">
                    <div className="loading-state" role="status" aria-live="polite">
                        <div className="loading-spinner" aria-hidden="true"></div>
                        <p>Завантаження товару</p>
                    </div>
                </main>
            </Layout>
        );
    }

    // Error state
    if (error) {
        return (
            <Layout>
                <SEO noindex />
                <main className="product-detail-content">
                    <div className="error-state" role="alert">
                        <h2>Помилка завантаження</h2>
                        <p>{error}</p>
                        <button
                            onClick={() => window.location.reload()}
                            className="retry-button"
                            type="button"
                        >
                            Спробувати ще раз
                        </button>
                    </div>
                </main>
            </Layout>
        );
    }

    // Product not found
    if (!product) {
        return (
            <Layout>
                <SEO noindex />
                <main className="product-detail-content">
                    <div className="not-found-state">
                        <h2>Товар не знайдено</h2>
                        <p>Запитаний товар не існує або був видалений.</p>
                        <button
                            onClick={() => window.history.back()}
                            className="back-button"
                            type="button"
                        >
                            Повернутися назад
                        </button>
                    </div>
                </main>
            </Layout>
        );
    }

    return (
        <Layout>
            <SEO
                title={(() => {
                    const category = product.categoryNameSingular || product.categoryName;
                    // Якщо є прив'язка до авто — використовуємо марку/модель
                    // Якщо ні (напр. компресори) — використовуємо manufacturer
                    const carPart = [product.brandName, product.modelName].filter(Boolean).join(' ');
                    const specPart = carPart || product.manufacturer || '';
                    return [category, specPart].filter(Boolean).join(' ') + ' купити в Україні';
                })()}
                noSuffix
                description={(() => {
                    if (product.description_ua) return product.description_ua;
                    const yearEngine = [product.year, product.engine].filter(Boolean).join(', ');
                    const spec = yearEngine ? ` (${yearEngine})` : '';
                    const carOrManufacturer = [product.brandName, product.modelName].filter(Boolean).join(' ')
                        || (product.manufacturer ? `виробник ${product.manufacturer}` : '');
                    return `${product.name_ua}${spec} - купити в UnionAuto. ${product.categoryName || 'Автозапчастина'}${carOrManufacturer ? ` ${carOrManufacturer}` : ''} за ціною ${formatPrice(product.price)} грн. Оригінальна якість, гарантія, доставка по Україні.`;
                })()}
                canonical={`/products/${product.slug || slug}`}
                structuredData={{
                    "@context": "https://schema.org",
                    "@type": "Product",
                    "name": product.name_ua,
                    "description": product.description_ua || product.name_ua,
                    "image": product.images && product.images.length > 0 ? product.images : [product.photo],
                    "sku": product.numerkatalogowy || product.code1 || `UA-${product.id}`,
                    "mpn": product.numerkatalogowy || product.code1,
                    ...((product.brandName || product.manufacturer) ? {
                        "brand": {
                            "@type": "Brand",
                            "name": product.brandName || product.manufacturer
                        }
                    } : {}),
                    "offers": {
                        "@type": "Offer",
                        "url": typeof window !== 'undefined' ? window.location.href : `https://union-auto.com.ua/products/${product.slug || slug}`,
                        "priceCurrency": "UAH",
                        "price": product.price,
                        "itemCondition": "https://schema.org/UsedCondition",
                        "availability": "https://schema.org/InStock",
                        "hasMerchantReturnPolicy": {
                            "@type": "MerchantReturnPolicy",
                            "applicableCountry": "UA",
                            "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
                            "merchantReturnDays": 14,
                            "returnMethod": "https://schema.org/ReturnByMail",
                            "returnPolicyLink": "https://union-auto.com.ua/return-policy"
                        },
                        "seller": {
                            "@type": "Organization",
                            "name": "UnionAuto"
                        }
                    },
                    "category": product.categoryName || "Автозапчастини",
                    "additionalProperty": [
                        product.code1 && {
                            "@type": "PropertyValue",
                            "name": "Код 1",
                            "value": product.code1
                        },
                        product.code2 && {
                            "@type": "PropertyValue",
                            "name": "Код 2",
                            "value": product.code2
                        },
                        product.code3 && {
                            "@type": "PropertyValue",
                            "name": "Код 3",
                            "value": product.code3
                        },
                        product.numerkatalogowy && {
                            "@type": "PropertyValue",
                            "name": "Номер по каталогу",
                            "value": product.numerkatalogowy
                        }
                    ].filter(Boolean)
                }}
            />
            <Breadcrumbs
                items={(() => {
                    const catSlug   = product.categorySlug || product.categoryId;
                    const brandSlug = product.brandName?.toLowerCase().replace(/\s+/g, '-');
                    const modelSlug = product.modelName?.toLowerCase().replace(/\s+/g, '-');
                    const items = [{ name: 'Головна', path: '/' }];

                    if (product.categoryName) {
                        items.push({
                            name: product.categoryName,
                            path: catSlug ? `/zapchastyny/${catSlug}` : null,
                        });
                    }
                    if (product.brandName) {
                        items.push({
                            name: product.brandName,
                            path: catSlug && brandSlug
                                ? `/zapchastyny/${catSlug}/${brandSlug}`
                                : (brandSlug ? `/zapchastyny?brand=${brandSlug}` : null),
                        });
                    }
                    if (product.modelName) {
                        items.push({
                            name: product.modelName,
                            path: catSlug && brandSlug && modelSlug
                                ? `/zapchastyny/${catSlug}/${brandSlug}/${modelSlug}`
                                : null,
                        });
                    }
                    items.push({ name: product.name_ua });
                    return items;
                })()}
            />
            <article className="product-detail-content">
                {/* Основна інформація про товар */}
                <div className="product-monolith">
                    <header className="product-header">
                        <div className="product-gallery">
                            {/* Основне зображення */}
                            <div className="main-image-container">
                            <ProductImage
                                    src={productImages[currentImageIndex]}
                                    alt={`${product.name_ua}${product.numerkatalogowy ? ` (${product.numerkatalogowy})` : ''} - зображення ${currentImageIndex + 1}`}
                                className="product-image"
                                loading="lazy"
                                    onClick={() => openGallery(currentImageIndex)}
                                    style={{ cursor: productImages.length > 1 ? 'pointer' : 'default' }}
                                />
                                {productImages.length > 1 && (
                                    <button
                                        className="gallery-expand-button"
                                        onClick={() => openGallery(currentImageIndex)}
                                        aria-label="Відкрити галерею"
                                        type="button"
                                    >
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M13 13l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                        Переглянути всі фото ({productImages.length})
                                    </button>
                                )}
                        </div>

                            {/* Мініатюри */}
                            {productImages.length > 1 && (
                                <div className="thumbnail-strip">
                                    {productImages.map((image, index) => (
                                        <button
                                            key={index}
                                            className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                                            onClick={() => setCurrentImageIndex(index)}
                                            aria-label={`Переглянути зображення ${index + 1}`}
                                            type="button"
                                        >
                                            <ProductImage
                                                src={image}
                                                alt={`Мініатюра ${index + 1}`}
                                                loading="lazy"
                                            />
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Індикатори для мобільних */}
                            {productImages.length > 1 && (
                                <div className="image-indicators">
                                    {productImages.map((_, index) => (
                                        <button
                                            key={index}
                                            className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
                                            onClick={() => setCurrentImageIndex(index)}
                                            aria-label={`Зображення ${index + 1}`}
                                            type="button"
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="product-info">
                            <h1 className="product-title">{product.name_ua}</h1>

                            <div className="product-meta">
                                <div className="product-category">
                                    <span className="meta-label">Категорія:</span>
                                    <span className="meta-value">
                                        {product.categoryName || 'Не вказано'}
                                    </span>
                                </div>

                                <div className="product-condition">
                                    <span className="meta-label">Стан:</span>
                                    <span className="meta-value">вживаний</span>
                                </div>

                                {/* Секція з кодами товару */}
                                <div className="product-codes-section">
                                    <span className="meta-label">Коди товару:</span>
                                    <div className="product-codes">
                                        {product.code1 && (
                                            <div className="product-code-item">
                                                <span className="code-label">Код 1:</span>
                                                <code className="code-value">{product.code1}</code>
                                            </div>
                                        )}
                                        {product.code2 && (
                                            <div className="product-code-item">
                                                <span className="code-label">Код 2:</span>
                                                <code className="code-value">{product.code2}</code>
                                </div>
                            )}
                            {product.code3 && (
                                            <div className="product-code-item">
                                                <span className="code-label">Код 3:</span>
                                                <code className="code-value">{product.code3}</code>
                                            </div>
                                        )}
                                        {product.numerkatalogowy && (
                                            <div className="product-code-item">
                                                <span className="code-label">Каталог:</span>
                                                <code className="code-value">{product.numerkatalogowy}</code>
                                </div>
                            )}
                        </div>
                                </div>
                            </div>

                            <div className="product-purchase">
                                <div className="product-price">
                                    <span className="price-value">
                                        {formatPrice(product.price)}
                                    </span>
                                    <span className="price-currency">₴</span>
                                </div>
                            <button
                                className="purchase-button"
                                onClick={handleBuyClick}
                                type="button"
                                    aria-label={`Додати ${product.name_ua} до кошика`}
                            >
                                Додати до кошика
                            </button>
                        </div>
                </div>
                    </header>

                    {/* Додаткова інформація про товар - таб інтерфейс */}
                    <section className="product-details-section" aria-label="Детальна інформація про товар">
                        {/* Таби */}
                        <div className="product-tabs" role="tablist" aria-label="Інформація про товар">
                            {tabs.map((tab) => (
                                tab.id === 'reviews' ? (
                                    <Link
                                        key={tab.id}
                                        className="product-tab"
                                        to="/reviews"
                                    >
                                        {tab.title}
                                    </Link>
                                ) : (
                                    <button
                                        key={tab.id}
                                        className={`product-tab ${activeTab === tab.id ? 'active' : ''}`}
                                        onClick={() => switchTab(tab.id)}
                                        role="tab"
                                        aria-selected={activeTab === tab.id}
                                        aria-controls={`tab-panel-${tab.id}`}
                                        id={`tab-${tab.id}`}
                                        type="button"
                                    >
                                        {tab.title}
                                    </button>
                                )
                            ))}
                                </div>

                        {/* Контент табів */}
                        <div className="product-tab-content">
                            <div id="tab-panel-product" role="tabpanel" aria-labelledby="tab-product" style={{ display: activeTab === 'product' ? 'block' : 'none' }}>
                                <div className="tab-content-wrapper">
                                    <div className="product-description">
                                        {product.description_ua ? (
                                            <div
                                                className="description-html"
                                                dangerouslySetInnerHTML={{ __html: product.description_ua }}
                                            />
                                        ) : (
                                            <p className="no-description">Опис товару відсутній</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div id="tab-panel-delivery" role="tabpanel" aria-labelledby="tab-delivery" style={{ display: activeTab === 'delivery' ? 'block' : 'none' }}>
                                <div className="tab-content-wrapper">
                                    <div className="info-section">
                                        <h3 className="section-title">Доставка по Україні</h3>
                                        <p className="section-text">Доставка здійснюється по всій території України службою <strong>Нова пошта</strong> — у відділення, поштомат або кур'єром.</p>
                                        <p className="section-text">Вартість доставки — від <strong>80 грн.</strong> Визначається перевізником залежно від ваги та габаритів посилки.</p>
                                        <p className="section-text">Більшість замовлень надсилаються в день замовлення (до 15:00) або наступного робочого дня. Термін доставки — <strong>1–3 робочих дні</strong>.</p>
                                    </div>

                                    <div className="info-section">
                                        <h3 className="section-title">Способи оплати</h3>
                                        <ul className="payment-methods">
                                            <li><strong>Накладений платіж (післяплата)</strong> — оплата при отриманні у відділенні. Комісія Нової пошти: 2% + 20 грн.</li>
                                            <li><strong>Банківський переказ за реквізитами IBAN</strong> — без комісії з боку магазину.</li>
                                        </ul>
                                    </div>

                                    <div className="info-section payment-details">
                                        <h4 className="section-subtitle">Реквізити для оплати:</h4>
                                        <div className="bank-details">
                                            <p><strong>ФОП Фігура Тетяна Богданівна</strong></p>
                                            <p>Код ЄДРПОУ: <code>2586412525</code></p>
                                            <p>р/р: <code>UA493052990000026009041032053</code></p>
                                            <p>Банк: АТ КБ "ПРИВАТБАНК"</p>
                                            <p>МФО: <code>305299</code></p>
                                        </div>
                                    </div>
                                    <Link to="/payment" className="tab-more-link">Детальніше про оплату та доставку →</Link>
                                </div>
                            </div>
                            <div id="tab-panel-warranty" role="tabpanel" aria-labelledby="tab-warranty" style={{ display: activeTab === 'warranty' ? 'block' : 'none' }}>
                                <div className="tab-content-wrapper">
                                    <div className="info-section">
                                        <h3 className="section-title">Гарантія на товари</h3>
                                        <p className="section-text">
                                            Усі товари — вживані автозапчастини (б/в), зняті з автомобілів у справному стані.
                                            Гарантія від магазину — <strong>14 календарних днів</strong> з моменту отримання товару.
                                        </p>
                                        <p className="section-text">
                                            Гарантія поширюється на приховані несправності деталі, невідповідність заявленим характеристикам або номеру деталі.
                                            Не поширюється на механічні пошкодження після отримання, неправильний монтаж та природне зношування.
                                        </p>
                                    </div>
                                    <div className="info-section">
                                        <h3 className="section-title">Повернення</h3>
                                        <p className="section-text">
                                            Повернення або обмін — протягом <strong>14 календарних днів</strong> з моменту отримання.
                                            Товар має зберігати товарний вигляд та комплектність.
                                        </p>
                                        <p className="section-text">
                                            Повернення коштів — на банківську картку Покупця протягом <strong>3 робочих днів</strong> після отримання та перевірки товару.
                                        </p>
                                    </div>
                                    <Link to="/return-policy" className="tab-more-link">Детальніше про гарантію та повернення →</Link>
                                </div>
                            </div>
                            <div id="tab-panel-contacts" role="tabpanel" aria-labelledby="tab-contacts" style={{ display: activeTab === 'contacts' ? 'block' : 'none' }}>
                                <div className="contact-info-section">
                                    <div className="contact-group">
                                        <h4 className="contact-group-title">Зв'яжіться з нами</h4>
                                        <div className="contact-list">
                                            {COMPANY_PHONES.map(({ number, display }) => (
                                                <CopyPhoneText key={number} number={number} className="contact-link">
                                                    <FaPhone className="contact-icon" aria-hidden="true" />
                                                    <span className="contact-text">{display}</span>
                                                </CopyPhoneText>
                                            ))}
                                            <a href={`mailto:${COMPANY_EMAIL}`} className="contact-link">
                                                <FaEnvelope className="contact-icon" aria-hidden="true" />
                                                <span className="contact-text">{COMPANY_EMAIL}</span>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                                </div>
                    </section>
                </div> {/* Закриття product-monolith */}
            </article>

            {product.similarProducts?.length >= 2 && (
                <RelatedProducts
                    products={product.similarProducts}
                    modelName={product.modelName}
                    brandName={product.brandName}
                    categorySlug={product.categorySlug}
                />
            )}

            {/* Модальне вікно галереї */}
            {isGalleryOpen && (
                <div className="gallery-modal" role="dialog" aria-modal="true" aria-labelledby="gallery-title">
                    <div className="gallery-modal-backdrop" onClick={closeGallery} />

                    <div className="gallery-modal-content">
                        {/* Заголовок */}
                        <header className="gallery-modal-header">
                            <h2 id="gallery-title">{product.name_ua}</h2>
                            <button
                                className="gallery-close-button"
                                onClick={closeGallery}
                                aria-label="Закрити галерею"
                                type="button"
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                        </header>

                        {/* Основне зображення */}
                        <div className="gallery-main-image">
                            <ProductImage
                                src={productImages[currentImageIndex]}
                                alt={`${product.name_ua}${product.numerkatalogowy ? ` (${product.numerkatalogowy})` : ''} - зображення ${currentImageIndex + 1}`}
                                className="gallery-full-image"
                            />

                            {/* Навігаційні кнопки */}
                            {productImages.length > 1 && (
                                <>
                                    <button
                                        className="gallery-nav-button prev"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            prevImage();
                                        }}
                                        aria-label="Попереднє зображення"
                                        type="button"
                                    >
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </button>

                                    <button
                                        className="gallery-nav-button next"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            nextImage();
                                        }}
                                        aria-label="Наступне зображення"
                                        type="button"
                                    >
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </button>
                                </>
                            )}
                        </div>

                        {/* Мініатюри в модальному вікні */}
                        {productImages.length > 1 && (
                            <div className="gallery-thumbnails">
                                {productImages.map((image, index) => (
                                    <button
                                        key={index}
                                        className={`gallery-thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                                        onClick={() => setCurrentImageIndex(index)}
                                        aria-label={`Переглянути зображення ${index + 1}`}
                                        type="button"
                                    >
                                        <ProductImage
                                            src={image}
                                            alt={`Мініатюра ${index + 1}`}
                                            loading="lazy"
                                        />
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Лічильник зображень */}
                        {productImages.length > 1 && (
                            <div className="gallery-counter">
                                {currentImageIndex + 1} / {productImages.length}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default ProductDetailPage;
